import { all, takeEvery, take, call, put, fork } from 'redux-saga/effects';
import { Action } from 'redux-actions';
import { PapiDeviation } from '@wix/da-papi-types';
import {
  uploadFile,
  uploadFiles,
  uploadFailure,
  uploadSuccess,
  uploadProgress,
  uploadFileImmediately,
  cancelFileUpload,
} from '../actions';
import { UploadFilePayload, UploadType } from '../types';
import { MAX_FILE_SIZE } from '../constants';
import { createUploadChannel } from './base';
import { uploadQueueWatcher } from './queue';
import { putDeviation, putErrorNotification } from '../../../utils/saga';
import i18next from 'i18next';
import { getServerInstance } from '@wix/da-ddt';

const ddt = getServerInstance();

const MEDIA_FOLDER_NAME = 'Media Uploads';

function* createDeviationUploadChannel({
  file,
  uploadType = UploadType.DEVIATION_NEW,
  deviationId,
  originalsDeviationId,
  originalsResponseId,
  folderId,
  folderName,
  useDefaults,
}) {
  const baseUrl = 'deviation/submit/upload';
  const formData = new FormData();
  let url;

  switch (uploadType) {
    case UploadType.DEVIATION_NEW:
      url = `${baseUrl}/deviation`;
      formData.append('upload_file', file);
      formData.append('use_defaults', useDefaults ? 'true' : 'false');
      if (folderId) {
        formData.append('folderid', folderId);
      } else if (folderName) {
        formData.append('folder_name', folderName);
      } else {
        // If no folder is specified, default to Media Uploads
        formData.append('folder_name', MEDIA_FOLDER_NAME);
      }
      break;
    case UploadType.DEVIATION_REPLACE_ORIGINAL:
      url = `${baseUrl}/replace_original`;
      formData.append('original_file', file);
      formData.append('deviationid', deviationId);
      break;
    case UploadType.DEVIATION_REPLACE_PREVIEW:
      url = `${baseUrl}/replace_preview`;
      formData.append('preview_file', file);
      formData.append('deviationid', deviationId);
      break;
    case UploadType.ORIGINALS_DELIVERY:
      url = `${baseUrl}/originals_delivery`;
      formData.append('upload_file', file);
      formData.append('originals_deviationid', originalsDeviationId);
      formData.append('responseid', originalsResponseId);
      formData.append('use_defaults', useDefaults ? 'true' : 'false');
      break;
    default:
      throw new Error(`Unsupported deviation upload type: ${uploadType}`);
  }

  return yield call(createUploadChannel, url, formData, uploadType);
}

function isHEIC(file: File): boolean {
  const heicMimeType = 'image/heic';
  const heicExtensions = ['heic', 'heif'];
  const fileType = file.type.toLowerCase();
  const fileExtension = file.name.split('.').pop()?.toLowerCase();

  return (
    fileType === heicMimeType ||
    (!!fileExtension && heicExtensions.includes(fileExtension))
  );
}

async function convertHEICtoPNG(file: File): Promise<File | Error> {
  if (typeof window === 'undefined') {
    return new Error('convertHEICtoPNG should only be called in the browser');
  }

  let heicTo: (args: { blob: Blob; type: string }) => Promise<Blob>;
  try {
    ({ heicTo } = await import('heic-to'));
  } catch (e) {
    return new Error('Failed to import heic-to');
  }

  try {
    const conversionResult = await heicTo({
      blob: file,
      type: 'image/png',
    });

    if (!(conversionResult instanceof Blob)) {
      return new Error('Failed to convert HEIC');
    }

    const pngFile = new File(
      [conversionResult],
      file.name.replace(/\.[^/.]+$/, '.png'),
      { type: 'image/png' }
    );
    return pngFile;
  } catch (e) {
    return new Error('Failed to convert HEIC');
  }
}

function* uploadFileHandler(action: Action<UploadFilePayload>) {
  const {
    file,
    uploadId,
    uploadType,
    deviationId,
    folderId,
    folderName,
    originalsDeviationId,
    originalsResponseId,
    useDefaults,
    onSuccess,
    onFailure,
  } = action.payload;

  const fileToUpload = isHEIC(file) ? yield call(convertHEICtoPNG, file) : file;

  if (fileToUpload instanceof Error) {
    ddt.alert('submit', 'Failed to convert HEIC to PNG', ['submit'], {});
    yield putErrorNotification(i18next.t('upload.error.heic'));
    yield put(
      uploadFailure(uploadId, {
        isServerError: true,
        name: 'HEIC',
        message: i18next.t('upload.error.heic'),
      })
    );
    return;
  }

  if (!fileToUpload || !(fileToUpload instanceof File)) {
    yield putErrorNotification(i18next.t('upload.error.invalidFile'));
    yield put(
      uploadFailure(uploadId, {
        isServerError: true,
        name: 'INVALID_FILE',
        message: i18next.t('upload.error.invalidFile'),
      })
    );
    return;
  }

  if (fileToUpload.size && fileToUpload.size > MAX_FILE_SIZE) {
    yield putErrorNotification(i18next.t('upload.error.tooBig'));
    yield put(
      uploadFailure(uploadId, {
        isServerError: true,
        name: 'SIZE',
        message: i18next.t('upload.error.tooBig'),
      })
    );
    return;
  }

  const channel = yield call(createDeviationUploadChannel, {
    file: fileToUpload,
    uploadType,
    deviationId,
    originalsDeviationId,
    originalsResponseId,
    folderId,
    folderName,
    useDefaults,
  });

  // If a cancel action for this file id is fired we close the channel
  yield fork(function* () {
    yield take(
      ({ type, payload }) =>
        type === cancelFileUpload.toString() && payload.uploadId === uploadId
    );
    channel.close();
  });

  while (true) {
    const { progress = 0, error, success, response } = yield take(channel);
    if (error) {
      yield put(uploadFailure(uploadId, error));
      if (onFailure) {
        yield call(onFailure, { uploadId }, error);
      }
      return;
    }
    if (success) {
      const deviation: PapiDeviation | undefined = response?.deviation;
      const uploadedDeviationId =
        deviation?.deviationId ?? response?.privateId ?? deviationId;

      yield put(
        uploadSuccess({
          uploadId,
          deviationId: uploadedDeviationId,
          deviation,
        })
      );

      if (deviation) {
        yield putDeviation(deviation);
      }

      if (onSuccess) {
        yield call(onSuccess, {
          uploadId,
          file: fileToUpload,
          deviationId: uploadedDeviationId,
          deviation,
        });
      }
      return;
    }

    yield put(uploadProgress(uploadId, progress));
  }
}

export default function* saga() {
  yield all([
    takeEvery(uploadFileImmediately, uploadFileHandler),
    uploadQueueWatcher(
      'main',
      [uploadFile, uploadFiles, uploadSuccess, uploadFailure],
      uploadFileImmediately,
      true // turn on sequenced uploads
    ),
  ]);
}
